import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  Grid,
  Chip,
  Button,
  Divider,
  LinearProgress,
  Card,
  CardContent,
  CardHeader,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Tooltip,
  Alert,
  useTheme,
} from "@mui/material";
import {
  Refresh as RefreshIcon,
  Stop as StopIcon,
  Check as CheckIcon,
  Error as ErrorIcon,
  PlayArrow as PlayArrowIcon,
  HourglassEmpty as PendingIcon,
  ChevronRight as ChevronRightIcon,
  Edit as EditIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  CompareArrows as CompareArrowsIcon,
  Timer as TimerIcon,
  ErrorOutline as ErrorOutlineIcon,
  Assignment as AssignmentIcon,
} from "@mui/icons-material";
import PageHeader from "../../components/common/PageHeader";
import EvaluationForm from "../../components/forms/EvaluationForm";
import AlertMessage from "../../components/common/AlertMessage";
import LoadingBackdrop from "../../components/common/LoadingBackdrop";
import ConfirmDialog from "../../components/common/ConfirmDialog";
import EvaluationResults from "../../components/visualizations/EvaluationResults";
import EvaluationResultsModal from "../../components/common/EvaluationResultsModal";
import { useAlert } from "../../context/AlertContext";
import api from "../../services/api";
import { Evaluation } from "../../types";
import { formatDateTime, formatDuration } from "../../utils/formatters";

const EvaluationDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const alert = useAlert();
  const isNew = id === "new";

  console.log("here is the id", id, isNew);

  // State variables
  const [evaluation, setEvaluation] = useState<Evaluation | null>(null);
  const [loading, setLoading] = useState<boolean>(!isNew);
  const [error, setError] = useState<string | null>(null);
  const [abortDialogOpen, setAbortDialogOpen] = useState<boolean>(false);
  const [isAborting, setIsAborting] = useState<boolean>(false);
  const [refreshInterval, setRefreshInterval] = useState<NodeJS.Timeout | null>(
    null,
  );
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [showResultsModal, setShowResultsModal] = useState<boolean>(false);
  const [prevStatus, setPrevStatus] = useState<string | null>(null);

  const theme = useTheme();

  // Fetch evaluation if viewing or editing
  useEffect(() => {
    if (isNew) return;

    const fetchEvaluation = async () => {
      try {
        setLoading(true);
        const response = await api.evaluations.getById(id as string);
        setEvaluation(response.data);
        setError(null);
      } catch (error) {
        console.error("Error fetching evaluation:", error);
        setError("Failed to load evaluation. Please try again.");
        alert.error("Failed to fetch evaluation");
      } finally {
        setLoading(false);
      }
    };

    fetchEvaluation();

    // Set up refresh interval for running evaluations
    const interval = setInterval(async () => {
      try {
        const response = await api.evaluations.getById(id as string);
        setEvaluation(response.data);

        // Stop refreshing when evaluation is completed or failed
        if (
          response.data.status === "completed" ||
          response.data.status === "failed"
        ) {
          clearInterval(interval);
          setRefreshInterval(null);
        }
      } catch (error) {
        console.error("Error refreshing evaluation:", error);
        // Don't show alerts for background refreshes
      }
    }, 5000);

    setRefreshInterval(interval);

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [id, isNew, alert]);

  // Modified fetchEvaluation to detect when an evaluation completes
  const fetchEvaluation = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await api.evaluations.getById(id as string);
      const fetchedEvaluation = response.data;

      // Store previous status to detect completion
      if (evaluation) {
        setPrevStatus(evaluation.status);
      }

      setEvaluation(fetchedEvaluation);

      // Show results modal when evaluation completes
      if (
        prevStatus === "running" &&
        fetchedEvaluation.status === "completed" &&
        fetchedEvaluation.results
      ) {
        setShowResultsModal(true);
      }
    } catch (error: any) {
      console.error("Error fetching evaluation:", error);
      setError(
        error.customMessage ||
          error.message ||
          "Error fetching evaluation details",
      );
    } finally {
      setLoading(false);
    }
  };

  // Helper function to get status chip
  const getStatusChip = (status: string) => {
    let color: "success" | "primary" | "warning" | "error" | "default" =
      "default";
    let label = status;
    let icon;

    switch (status) {
      case "completed":
        color = "success";
        label = "Completed";
        icon = <CheckIcon />;
        break;
      case "pending":
        color = "default";
        label = "Pending";
        icon = <PendingIcon />;
        break;
      case "running":
        color = "primary";
        label = "Running";
        icon = <PlayArrowIcon />;
        break;
      case "failed":
        color = "error";
        label = "Failed";
        icon = <ErrorIcon />;
        break;
    }

    return (
      <Chip
        label={label}
        color={color}
        icon={icon}
        sx={{ fontWeight: "medium" }}
      />
    );
  };

  // Handle refresh
  const handleRefresh = async () => {
    if (!evaluation) return;

    try {
      setLoading(true);
      const response = await api.evaluations.getById(evaluation._id);
      setEvaluation(response.data);
      alert.success("Evaluation refreshed");
    } catch (error) {
      console.error("Error refreshing evaluation:", error);
      alert.error("Failed to refresh evaluation");
    } finally {
      setLoading(false);
    }
  };

  // Handle retry
  const handleRetry = async () => {
    if (!evaluation) return;

    try {
      setLoading(true);
      const response = await api.evaluations.retry(evaluation._id);
      navigate(`/evaluations/${response.data._id}`);
      alert.success("New evaluation created and started");
    } catch (error) {
      console.error("Error retrying evaluation:", error);
      alert.error("Failed to retry evaluation");
    } finally {
      setLoading(false);
    }
  };

  // Handle edit
  const handleEdit = () => {
    setIsEditing(true);
  };

  // Handle cancel edit
  const handleCancelEdit = () => {
    setIsEditing(false);
  };

  // Handle abort
  const handleAbort = () => {
    if (!evaluation) return;

    if (evaluation.status !== "running" && evaluation.status !== "pending") {
      alert.info("Only running or pending evaluations can be aborted");
      return;
    }

    setAbortDialogOpen(true);
  };

  // Confirm abort
  const confirmAbort = async () => {
    if (!evaluation) return;

    try {
      setIsAborting(true);
      await api.evaluations.abort(evaluation._id);

      // Refresh evaluation data
      const response = await api.evaluations.getById(id as string);
      setEvaluation(response.data);

      alert.success("Evaluation aborted successfully");
    } catch (error) {
      console.error("Error aborting evaluation:", error);
      alert.error("Failed to abort evaluation");
    } finally {
      setIsAborting(false);
      setAbortDialogOpen(false);
    }
  };

  // Calculate runtime duration
  const calculateRuntime = (evaluation: Evaluation): string => {
    if (!evaluation.startedAt) return "Not started";

    const startTime = new Date(evaluation.startedAt).getTime();
    const endTime = evaluation.completedAt
      ? new Date(evaluation.completedAt).getTime()
      : Date.now();

    return formatDuration(endTime - startTime);
  };

  // Calculate success rate from results
  const calculateSuccessRate = (evaluation: Evaluation): number => {
    if (!evaluation.results || !evaluation.results.results) return 0;

    const results = evaluation.results.results as any[];
    if (results.length === 0) return 0;

    const successCount = results.filter((r) => r.success).length;
    return successCount / results.length;
  };

  // Add a closeResultsModal function
  const closeResultsModal = () => {
    setShowResultsModal(false);
  };

  // Extract model data from results - similar to EvaluationResultsModal
  const getModelResults = () => {
    if (!evaluation?.results || !evaluation.results.results) {
      return [];
    }

    const results = evaluation.results.results as any[];
    const providerResults = new Map<
      string,
      { score: number; latency: number; tokenUsage: number; cost: number }
    >();

    results.forEach((result) => {
      // Safely extract provider name ensuring it's a string
      const providerId = result.provider?.id || "unknown";
      const provider =
        evaluation.providers?.find((p) => p._id === providerId)?.name ||
        providerId;

      if (!providerResults.has(provider)) {
        providerResults.set(provider, {
          score: 0,
          latency: 0,
          tokenUsage: 0,
          cost: 0,
        });
      }

      const currentData = providerResults.get(provider)!;
      providerResults.set(provider, {
        score: currentData.score + (result.score || 0),
        latency: currentData.latency + (result.latencyMs || 0),
        tokenUsage:
          currentData.tokenUsage + (result.response?.tokenUsage?.total || 0),
        cost: currentData.cost + (result.cost || 0),
      });
    });

    // Calculate averages
    const providers = evaluation.providers || [];
    return Array.from(providerResults.entries()).map(([providerId, data]) => {
      const provider = providers.find(
        (p) => p._id === providerId || p.name === providerId,
      );
      const totalTests = results.filter((r) => {
        const provId = r.provider?.id || "";
        return (
          provId === providerId ||
          providers.find((p) => p._id === provId)?.name === providerId
        );
      }).length;

      return {
        id: providerId,
        name: provider?.name || providerId,
        score: totalTests > 0 ? data.score / totalTests : 0,
        latency: totalTests > 0 ? data.latency / totalTests : 0,
        tokenUsage: data.tokenUsage,
        cost: data.cost,
      };
    });
  };

  // Find key differences between models - similar to EvaluationResultsModal
  const findKeyDifferences = () => {
    const modelResults = getModelResults();
    if (modelResults.length < 2) return [];

    const differences = [];

    // Compare latency
    if (Math.abs(modelResults[0].latency - modelResults[1].latency) > 1000) {
      differences.push(
        `${modelResults[0].name} was ${
          modelResults[0].latency < modelResults[1].latency
            ? `${Math.round((modelResults[1].latency / modelResults[0].latency) * 10) / 10}x faster`
            : `${Math.round((modelResults[0].latency / modelResults[1].latency) * 10) / 10}x slower`
        } than ${modelResults[1].name}`,
      );
    }

    // Compare cost
    if (Math.abs(modelResults[0].cost / modelResults[1].cost) > 5) {
      differences.push(
        `${modelResults[0].name} was ${
          modelResults[0].cost > modelResults[1].cost
            ? `${Math.round((modelResults[0].cost / modelResults[1].cost) * 10) / 10}x more expensive`
            : `${Math.round((modelResults[1].cost / modelResults[0].cost) * 10) / 10}x cheaper`
        } than ${modelResults[1].name}`,
      );
    }

    // Compare token usage
    if (
      Math.abs(modelResults[0].tokenUsage - modelResults[1].tokenUsage) > 50
    ) {
      differences.push(
        `${modelResults[0].name} used ${
          modelResults[0].tokenUsage > modelResults[1].tokenUsage
            ? `${Math.round(modelResults[0].tokenUsage - modelResults[1].tokenUsage)} more`
            : `${Math.round(modelResults[1].tokenUsage - modelResults[0].tokenUsage)} fewer`
        } tokens than ${modelResults[1].name}`,
      );
    }

    return differences;
  };

  // Get failed tests with their error messages - similar to EvaluationResultsModal
  const getFailedTests = () => {
    if (!evaluation?.results || !evaluation.results.results) {
      return [];
    }

    const results = evaluation.results.results as any[];
    return results.filter((result) => !result.success);
  };

  // Get test metrics
  const getTestMetrics = () => {
    if (!evaluation?.results || !evaluation.results.results) {
      return { passed: 0, failed: 0, total: 0, passRate: 0 };
    }

    const results = evaluation.results.results as any[];
    const passed = results.filter((result) => result.success).length;
    const failed = results.filter((result) => !result.success).length;
    const total = results.length;
    const passRate = total > 0 ? passed / total : 0;

    return { passed, failed, total, passRate };
  };

  return (
    <Box>
      <PageHeader
        title={isNew ? "New Evaluation" : "Evaluation Details"}
        subtitle={
          isNew
            ? "Create and run a new evaluation"
            : `View evaluation details and results`
        }
        action={
          isNew ? (
            <Button
              variant="outlined"
              color="inherit"
              onClick={() => navigate("/evaluations")}
            >
              Cancel
            </Button>
          ) : (
            <Box sx={{ display: "flex", gap: 1 }}>
              {!isEditing && (
                <>
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<RefreshIcon />}
                    onClick={handleRefresh}
                    disabled={loading}
                  >
                    Refresh
                  </Button>
                  {(evaluation?.status === "completed" ||
                    evaluation?.status === "failed") && (
                    <>
                      <Button
                        variant="outlined"
                        color="success"
                        startIcon={<PlayArrowIcon />}
                        onClick={handleRetry}
                        disabled={loading}
                      >
                        Retry
                      </Button>
                      <Button
                        variant="outlined"
                        color="primary"
                        startIcon={<EditIcon />}
                        onClick={handleEdit}
                        disabled={loading}
                      >
                        Edit
                      </Button>
                    </>
                  )}
                  {(evaluation?.status === "running" ||
                    evaluation?.status === "pending") && (
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<StopIcon />}
                      onClick={handleAbort}
                      disabled={loading}
                    >
                      Abort
                    </Button>
                  )}
                </>
              )}
              {isEditing && (
                <Button
                  variant="outlined"
                  color="inherit"
                  onClick={handleCancelEdit}
                >
                  Cancel Edit
                </Button>
              )}
            </Box>
          )
        }
        breadcrumbs={[
          { label: "Dashboard", path: "/" },
          { label: "Evaluations", path: "/evaluations" },
          { label: isNew ? "New" : "Details", path: "" },
        ]}
      />

      <LoadingBackdrop open={loading && !evaluation && !isNew} />

      {error && (
        <AlertMessage
          open={Boolean(error)}
          onClose={() => setError(null)}
          severity="error"
          title="Error"
          message={error}
        />
      )}

      {isNew || isEditing ? (
        <EvaluationForm
          initialData={
            isEditing && evaluation ? (evaluation as Evaluation) : undefined
          }
          isEdit={isEditing}
        />
      ) : (
        evaluation && (
          <Grid container spacing={3}>
            {/* Summary Card */}
            <Grid item xs={12} md={4}>
              <Card sx={{ mb: 3, borderRadius: 2 }}>
                <CardHeader
                  title="Evaluation Summary"
                  action={
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <Tooltip title="Edit evaluation">
                        <IconButton
                          onClick={handleEdit}
                          disabled={
                            evaluation.status === "running" ||
                            evaluation.status === "pending"
                          }
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Refresh">
                        <IconButton onClick={handleRefresh}>
                          <RefreshIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  }
                />
                <Divider />
                <CardContent>
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant="subtitle2"
                      color="text.secondary"
                      gutterBottom
                    >
                      Status
                    </Typography>
                    {getStatusChip(evaluation.status)}
                  </Box>

                  {evaluation.status === "running" && (
                    <Box sx={{ mb: 2 }}>
                      <Typography
                        variant="subtitle2"
                        color="text.secondary"
                        gutterBottom
                      >
                        Progress
                      </Typography>
                      <LinearProgress
                        variant="indeterminate"
                        sx={{ height: 8, borderRadius: 4 }}
                      />
                    </Box>
                  )}

                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={6}>
                      <Typography
                        variant="subtitle2"
                        color="text.secondary"
                        gutterBottom
                      >
                        Tests
                      </Typography>
                      <Typography>{evaluation.testIds?.length || 0}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography
                        variant="subtitle2"
                        color="text.secondary"
                        gutterBottom
                      >
                        Prompts
                      </Typography>
                      <Typography>
                        {evaluation.promptIds?.length || 0}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography
                        variant="subtitle2"
                        color="text.secondary"
                        gutterBottom
                      >
                        Providers
                      </Typography>
                      <Typography>
                        {evaluation.providerIds?.length || 0}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography
                        variant="subtitle2"
                        color="text.secondary"
                        gutterBottom
                      >
                        Duration
                      </Typography>
                      <Typography>
                        {evaluation.completedAt && evaluation.startedAt
                          ? calculateRuntime(evaluation)
                          : "Not completed"}
                      </Typography>
                    </Grid>
                  </Grid>

                  <Box sx={{ mt: 3 }}>
                    {evaluation.status === "running" ||
                    evaluation.status === "pending" ? (
                      <Button
                        variant="outlined"
                        startIcon={<StopIcon />}
                        onClick={handleAbort}
                        fullWidth
                      >
                        Abort evaluation
                      </Button>
                    ) : (
                      <Button
                        variant="contained"
                        startIcon={<RefreshIcon />}
                        onClick={handleRetry}
                        fullWidth
                      >
                        Retry evaluation
                      </Button>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Results Card */}
            <Grid item xs={12} md={8}>
              <Card sx={{ mb: 3, borderRadius: 2 }}>
                <CardHeader
                  title="Results Overview"
                  action={
                    <Chip
                      label={
                        evaluation.status === "completed"
                          ? `${Math.round(calculateSuccessRate(evaluation) * 100)}% Success Rate`
                          : "No results yet"
                      }
                      color={
                        calculateSuccessRate(evaluation) > 0.8
                          ? "success"
                          : calculateSuccessRate(evaluation) > 0.5
                            ? "warning"
                            : "error"
                      }
                      sx={{ fontWeight: "bold" }}
                    />
                  }
                />
                <Divider />
                <CardContent>
                  {evaluation.status !== "completed" ? (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        py: 4,
                      }}
                    >
                      <Typography
                        variant="subtitle1"
                        color="text.secondary"
                        gutterBottom
                      >
                        {evaluation.status === "pending"
                          ? "Evaluation is pending"
                          : evaluation.status === "running"
                            ? "Evaluation is running"
                            : "Evaluation failed"}
                      </Typography>
                      {evaluation.status === "running" && (
                        <LinearProgress
                          sx={{ width: "50%", mt: 2 }}
                          variant="indeterminate"
                        />
                      )}
                      {evaluation.status === "failed" && evaluation.error && (
                        <Alert severity="error" sx={{ mt: 2, width: "100%" }}>
                          {evaluation.error}
                        </Alert>
                      )}
                    </Box>
                  ) : (
                    <>
                      {/* Test Metrics Summary */}
                      <Box sx={{ mb: 3 }}>
                        <Paper
                          variant="outlined"
                          sx={{
                            p: 2,
                            bgcolor: theme.palette.background.default,
                          }}
                        >
                          <Grid container spacing={2}>
                            <Grid item xs={12} sm={4}>
                              <Box sx={{ textAlign: "center" }}>
                                <Typography variant="h5" color="success.main">
                                  {getTestMetrics().passed}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  Tests Passed
                                </Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                              <Box sx={{ textAlign: "center" }}>
                                <Typography variant="h5" color="error.main">
                                  {getTestMetrics().failed}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  Tests Failed
                                </Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                              <Box sx={{ textAlign: "center" }}>
                                <Typography variant="h5">
                                  {Math.round(getTestMetrics().passRate * 100)}%
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  Pass Rate
                                </Typography>
                              </Box>
                            </Grid>
                          </Grid>
                        </Paper>
                      </Box>

                      {/* Model Performance Section */}
                      <Box sx={{ mb: 3 }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            mb: 1,
                          }}
                        >
                          <CheckCircleOutlineIcon
                            color="primary"
                            sx={{ mr: 1 }}
                          />
                          <Typography variant="subtitle1">
                            Model Performance
                          </Typography>
                        </Box>
                        <Paper
                          variant="outlined"
                          sx={{
                            p: 2,
                            bgcolor: theme.palette.background.default,
                          }}
                        >
                          <Grid container spacing={2}>
                            {getModelResults().map((model, index) => (
                              <Grid item xs={12} key={index}>
                                <Box
                                  sx={{
                                    p: 1,
                                    borderRadius: 1,
                                    bgcolor: theme.palette.background.paper,
                                  }}
                                >
                                  <Typography variant="subtitle2" gutterBottom>
                                    {model.name}
                                  </Typography>
                                  <Grid container spacing={2}>
                                    <Grid item xs={6} sm={3}>
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                      >
                                        Success Rate:
                                      </Typography>
                                      <Typography variant="body2">
                                        {Math.round(model.score * 100)}%
                                      </Typography>
                                    </Grid>
                                    <Grid item xs={6} sm={3}>
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                      >
                                        Avg. Latency:
                                      </Typography>
                                      <Typography variant="body2">
                                        {(model.latency / 1000).toFixed(2)}s
                                      </Typography>
                                    </Grid>
                                    <Grid item xs={6} sm={3}>
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                      >
                                        Token Usage:
                                      </Typography>
                                      <Typography variant="body2">
                                        {Math.round(model.tokenUsage)} tokens
                                      </Typography>
                                    </Grid>
                                    <Grid item xs={6} sm={3}>
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                      >
                                        Cost:
                                      </Typography>
                                      <Typography variant="body2">
                                        ${model.cost.toFixed(5)}
                                      </Typography>
                                    </Grid>
                                  </Grid>
                                </Box>
                              </Grid>
                            ))}
                          </Grid>
                        </Paper>
                      </Box>

                      {/* Key Differences Section */}
                      <Box sx={{ mb: 3 }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            mb: 1,
                          }}
                        >
                          <CompareArrowsIcon color="primary" sx={{ mr: 1 }} />
                          <Typography variant="subtitle1">
                            Key Differences
                          </Typography>
                        </Box>
                        <Paper
                          variant="outlined"
                          sx={{
                            p: 2,
                            bgcolor: theme.palette.background.default,
                          }}
                        >
                          {findKeyDifferences().length > 0 ? (
                            <ul style={{ margin: 0, paddingLeft: "1.5rem" }}>
                              {findKeyDifferences().map((diff, index) => (
                                <li key={index}>
                                  <Typography variant="body2">
                                    {diff}
                                  </Typography>
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              No significant differences detected between models
                            </Typography>
                          )}
                        </Paper>
                      </Box>

                      {/* Failed Tests Section */}
                      {getFailedTests().length > 0 && (
                        <Box sx={{ mb: 3 }}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              mb: 1,
                            }}
                          >
                            <ErrorOutlineIcon color="error" sx={{ mr: 1 }} />
                            <Typography variant="subtitle1">
                              Failed Assertions ({getFailedTests().length})
                            </Typography>
                          </Box>
                          <Paper
                            variant="outlined"
                            sx={{
                              p: 2,
                              bgcolor: theme.palette.background.default,
                            }}
                          >
                            <List disablePadding>
                              {getFailedTests().map((test, index) => (
                                <ListItem
                                  key={index}
                                  disablePadding
                                  sx={{ mb: 2 }}
                                >
                                  <Box sx={{ width: "100%" }}>
                                    <Alert
                                      severity="error"
                                      sx={{ mb: 1 }}
                                      variant="outlined"
                                    >
                                      <Typography variant="subtitle2">
                                        Failure Reason:
                                      </Typography>
                                      <Typography variant="body2">
                                        {test.error ||
                                          test.gradingResult?.reason ||
                                          "Test failed"}
                                      </Typography>
                                    </Alert>

                                    {test.testCase?.assert && (
                                      <Box sx={{ mt: 2, mb: 1 }}>
                                        <Box
                                          sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            mb: 0.5,
                                          }}
                                        >
                                          <AssignmentIcon
                                            fontSize="small"
                                            sx={{ mr: 0.5 }}
                                          />
                                          <Typography variant="subtitle2">
                                            Expected Behavior:
                                          </Typography>
                                        </Box>
                                        <List disablePadding dense>
                                          {Array.isArray(
                                            test.testCase.assert,
                                          ) &&
                                            test.testCase.assert.map(
                                              (
                                                assertion: any,
                                                assertIdx: number,
                                              ) => (
                                                <ListItem
                                                  key={assertIdx}
                                                  sx={{ pl: 2, py: 0.5 }}
                                                >
                                                  <ListItemText
                                                    primary={`${assertion.type}: ${assertion.value}`}
                                                  />
                                                </ListItem>
                                              ),
                                            )}
                                        </List>
                                      </Box>
                                    )}

                                    <Box sx={{ mt: 1 }}>
                                      <Typography
                                        variant="subtitle2"
                                        gutterBottom
                                      >
                                        Model Output:
                                      </Typography>
                                      <Paper
                                        variant="outlined"
                                        sx={{
                                          p: 1.5,
                                          backgroundColor:
                                            theme.palette.mode === "dark"
                                              ? theme.palette.background.paper
                                              : theme.palette.grey[50],
                                          borderRadius: 1,
                                          maxHeight: "100px",
                                          overflow: "auto",
                                        }}
                                      >
                                        <Typography
                                          variant="body2"
                                          sx={{ fontFamily: "monospace" }}
                                        >
                                          {String(
                                            test.response?.output ||
                                              "No output",
                                          )}
                                        </Typography>
                                      </Paper>
                                    </Box>

                                    <Box sx={{ mt: 2 }}>
                                      <Typography
                                        variant="subtitle2"
                                        gutterBottom
                                      >
                                        Test Details:
                                      </Typography>
                                      <Grid container spacing={2}>
                                        <Grid item xs={6} md={3}>
                                          <Typography
                                            variant="caption"
                                            color="text.secondary"
                                          >
                                            Model:
                                          </Typography>
                                          <Typography variant="body2">
                                            {test.provider?.id ||
                                              "Unknown model"}
                                          </Typography>
                                        </Grid>
                                        <Grid item xs={6} md={3}>
                                          <Typography
                                            variant="caption"
                                            color="text.secondary"
                                          >
                                            Token Usage:
                                          </Typography>
                                          <Typography variant="body2">
                                            {test.response?.tokenUsage?.total ||
                                              0}{" "}
                                            tokens
                                          </Typography>
                                        </Grid>
                                        <Grid item xs={6} md={3}>
                                          <Typography
                                            variant="caption"
                                            color="text.secondary"
                                          >
                                            Latency:
                                          </Typography>
                                          <Typography variant="body2">
                                            {test.latencyMs
                                              ? `${(test.latencyMs / 1000).toFixed(2)}s`
                                              : "N/A"}
                                          </Typography>
                                        </Grid>
                                        <Grid item xs={6} md={3}>
                                          <Typography
                                            variant="caption"
                                            color="text.secondary"
                                          >
                                            Cost:
                                          </Typography>
                                          <Typography variant="body2">
                                            $
                                            {test.cost
                                              ? test.cost.toFixed(5)
                                              : "0.00000"}
                                          </Typography>
                                        </Grid>
                                      </Grid>
                                    </Box>

                                    {index < getFailedTests().length - 1 && (
                                      <Divider sx={{ my: 2 }} />
                                    )}
                                  </Box>
                                </ListItem>
                              ))}
                            </List>
                          </Paper>
                        </Box>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Detailed results visualization */}
            <Grid item xs={12}>
              {evaluation.status === "completed" && evaluation.results && (
                <EvaluationResults evaluation={evaluation} />
              )}
            </Grid>
          </Grid>
        )
      )}

      <ConfirmDialog
        open={abortDialogOpen}
        title="Abort Evaluation"
        message="Are you sure you want to abort this evaluation? This action cannot be undone."
        confirmButtonText="Abort"
        confirmButtonColor="error"
        onConfirm={confirmAbort}
        onCancel={() => setAbortDialogOpen(false)}
        loading={isAborting}
      />

      {evaluation && (
        <EvaluationResultsModal
          open={showResultsModal}
          onClose={closeResultsModal}
          evaluation={evaluation}
        />
      )}
    </Box>
  );
};

export default EvaluationDetail;
